import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function LoginPage({ adminMode = false }) {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [form,     setForm]     = useState({ email: '', password: '' });
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (adminMode && user.role !== 'admin') {
        toast.error('Admin access required.');
        return;
      }
      toast.success(`Welcome back, ${user.name}!`);
      navigate(user.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Left panel — visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-on-surface items-center justify-center relative overflow-hidden">
        {/* Bolt pattern background */}
        <svg className="absolute inset-0 w-full h-full opacity-5">
          <defs>
            <pattern id="bp" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="30" cy="30" r="10" fill="none" stroke="white" strokeWidth="1.5" />
              <circle cx="30" cy="30" r="4"  fill="white" />
              <line x1="30" y1="20" x2="30" y2="14" stroke="white" strokeWidth="1.5" />
              <line x1="30" y1="40" x2="30" y2="46" stroke="white" strokeWidth="1.5" />
              <line x1="20" y1="30" x2="14" y2="30" stroke="white" strokeWidth="1.5" />
              <line x1="40" y1="30" x2="46" y2="30" stroke="white" strokeWidth="1.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#bp)" />
        </svg>
        <div className="absolute left-0 top-0 w-2 h-full bg-primary-container" />
        <div className="relative z-10 text-center px-12">
          <h1 className="font-headline font-black text-5xl uppercase tracking-tighter text-white leading-none">
            Central<br/><span className="text-primary-container">Fasteners</span>
          </h1>
          <p className="font-label text-xs uppercase tracking-[0.3em] text-gray-500 mt-3">Industrial Precision</p>
          {adminMode && (
            <div className="mt-8 px-4 py-2 border border-primary-container/40 inline-flex items-center gap-2">
              <span className="material-symbols-outlined text-primary-container text-[18px]">admin_panel_settings</span>
              <span className="font-label text-xs uppercase tracking-widest text-gray-400">Admin Portal</span>
            </div>
          )}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-10">
            <Link to="/">
              <span className="font-headline font-black text-3xl uppercase tracking-tighter text-on-surface">
                Central <span className="text-primary-container">Fasteners</span>
              </span>
            </Link>
          </div>

          <div className="mb-8">
            {adminMode
              ? <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-primary-container flex items-center justify-center">
                    <span className="material-symbols-outlined text-white text-[18px]">admin_panel_settings</span>
                  </div>
                  <h2 className="font-headline font-black text-3xl uppercase tracking-tight">Admin Login</h2>
                </div>
              : <h2 className="font-headline font-black text-3xl uppercase tracking-tight">Sign In</h2>
            }
            <p className="font-body text-sm text-secondary mt-1">
              {adminMode ? 'Restricted access — admins only.' : 'Welcome back. Sign in to continue.'}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="input-label">Email Address</label>
              <input name="email" type="email" value={form.email} onChange={handle} required
                className="input-field" placeholder="you@example.com" autoComplete="email" />
            </div>

            <div>
              <label className="input-label">Password</label>
              <div className="relative">
                <input name="password" type={showPass ? 'text' : 'password'} value={form.password} onChange={handle} required
                  className="input-field pr-12" placeholder="••••••••" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary hover:text-primary-container transition-colors">
                  <span className="material-symbols-outlined text-[20px]">{showPass ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="btn-primary w-full justify-center py-4 text-base disabled:opacity-70">
              {loading
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Signing in...</>
                : <><span className="material-symbols-outlined">login</span> Sign In</>
              }
            </button>
          </form>

          {!adminMode && (
            <p className="font-body text-sm text-secondary text-center mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-container font-bold hover:underline">Register here</Link>
            </p>
          )}

          {!adminMode && (
            <p className="font-body text-xs text-secondary text-center mt-4">
              Admin?{' '}
              <Link to="/admin/login" className="text-secondary hover:text-primary-container transition-colors font-semibold">
                Admin login
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
